import React, { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, FileDown } from "lucide-react";
import { toast } from "sonner";

/**
 * Two-button group: Download template + Upload xlsx.
 *
 * Props:
 *   entity: one of "materials", "bop", "machines", "manpower", "parts"
 *   onComplete: called after successful upload to let parent refresh data
 */
export default function BulkUploadButton({ entity, onComplete }) {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const downloadTemplate = async () => {
        try {
            const res = await api.get(`/bulk/${entity}/template`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url; a.download = `${entity}_template.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Template download failed");
        }
    };

    const onPick = () => fileRef.current?.click();

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        setBusy(true);
        try {
            const { data } = await api.post(`/bulk/${entity}`, form, { headers: { "Content-Type": "multipart/form-data" } });
            const msg = `Inserted ${data.inserted}/${data.total_rows} rows · ${data.skipped} skipped`;
            if (data.skipped > 0) {
                toast.warning(`${msg}. First error: ${data.errors?.[0]?.error || "—"}`);
            } else {
                toast.success(msg);
            }
            onComplete?.();
        } catch (err) {
            const detail = err.response?.data?.detail || err.response?.data || err.message || "Upload failed";
            toast.error(typeof detail === "string" ? detail : JSON.stringify(detail), { duration: 8000 });
            // eslint-disable-next-line no-console
            console.error("bulk upload error:", err.response || err);
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <>
            <Button variant="outline" className="rounded-none" onClick={downloadTemplate} data-testid={`bulk-template-${entity}`}>
                <FileDown size={14} className="mr-2" /> Template
            </Button>
            <Button variant="outline" className="rounded-none" onClick={onPick} disabled={busy} data-testid={`bulk-upload-${entity}`}>
                <Upload size={14} className="mr-2" /> {busy ? "Uploading…" : "Bulk upload"}
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onFile} className="hidden" />
        </>
    );
}
